import {ContentSubmissionComment} from '@/types/contentSubmission';
import api from '../index';

type ContentSubmissionEventParams = {
  contentSubmissionId: string;
  brandMessage?: string;
};

function buildEventPayload(brandMessage?: string) {
  return {
    brand_message: brandMessage ?? null,
  };
}

function contentSubmissionSerializer(data: {data: Record<string, unknown>}) {
  return {
    data: {
      ...data.data,
      type: 'content-submissions',
    },
  };
}

const contentSubmissionUpdaters = {
  accept: async ({
    contentSubmissionId,
    brandMessage,
  }: ContentSubmissionEventParams) => {
    return api.put(
      `/content_submissions/${contentSubmissionId}/accept`,
      buildEventPayload(brandMessage),
      {
        params: {
          include:
            'contract.deliverables,campaign.campaign_content_submissions_metrics',
        },
        customSerializer: contentSubmissionSerializer,
      }
    );
  },

  archive: async ({
    contentSubmissionId,
    brandMessage,
  }: ContentSubmissionEventParams) => {
    return api.put(
      `/content_submissions/${contentSubmissionId}/archive`,
      buildEventPayload(brandMessage),
      {customSerializer: contentSubmissionSerializer}
    );
  },

  reject: async ({
    contentSubmissionId,
    brandMessage,
  }: ContentSubmissionEventParams) => {
    if (!brandMessage) {
      throw new Error('brandMessage is required for reject');
    }
    return api.put(
      `/content_submissions/${contentSubmissionId}/request_edit`,
      buildEventPayload(brandMessage),
      {customSerializer: contentSubmissionSerializer}
    );
  },

  unarchive: async ({contentSubmissionId}: ContentSubmissionEventParams) => {
    return api.put(
      `/content_submissions/${contentSubmissionId}/unarchive`,
      buildEventPayload(),
      {customSerializer: contentSubmissionSerializer}
    );
  },

  createApprovals: async ({
    contentSubmissionVersionId,
    steps,
  }: {
    contentSubmissionId: string;
    contentSubmissionVersionId: string;
    steps: string[];
  }) => {
    return api.post(
      `/content_submission_versions/${contentSubmissionVersionId}/approvals`,
      {steps},
      {
        customSerializer: (data: {data: object}) => ({
          data: {
            ...data.data,
            type: 'content-submission-approvals',
          },
        }),
      }
    );
  },

  addComment: async (comment: {
    content: string;
    email: string;
    fullName: string;
    createdAt?: string;
    step?: string;
    contentSubmissionVersion: {
      id: string;
      contentSubmissionComments: ContentSubmissionComment[];
    };
  }) => {
    return api.post(
      `/content_submission_comments`,
      {
        content: comment.content,
        email: comment.email,
        full_name: comment.fullName,
        created_at: comment.createdAt || new Date().toISOString(),
        ...(comment.step ? {step: comment.step} : {}),
      },
      {
        customSerializer: (data: any) => ({
          ...data,
          data: {
            ...data.data,
            relationships: {
              content_submission_version: {
                data: {
                  type: 'content-submission-versions',
                  id: comment.contentSubmissionVersion.id,
                },
              },
            },
          },
        }),
      }
    );
  },
};

export default contentSubmissionUpdaters;
